import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { Img, Reveal, SectionLabel, StatusBadge, ButtonLink } from "@/components/common";
import { useSite } from "@/lib/site";
import { useFetch } from "@/lib/api";
import { cn } from "@/lib/utils";

const SPANS = ["md:col-span-7", "md:col-span-5", "md:col-span-5", "md:col-span-7", "md:col-span-6", "md:col-span-6"];

const FeaturedCard = ({ project, index }) => {
  const { catById } = useSite();
  return (
    <Link to={`/work/${project.slug}`} data-testid={`featured-project-${project.slug}`} className="group relative block h-[380px] overflow-hidden rounded-md bg-surface md:h-[460px] lg:h-[580px]">
      <Img src={project.cover_image} alt={`${project.name} — featured work`} sizes="(max-width: 768px) 100vw, 60vw" className="absolute inset-0 h-full w-full object-cover object-top transition-transform duration-[1600ms] ease-out group-hover:scale-[1.05]" />
      <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/20 to-transparent" />
      <div className="absolute left-5 right-5 top-5 flex items-center justify-between">
        <StatusBadge status={project.status} />
        <span className="font-mono text-xs text-white/80">{String(index + 1).padStart(2, "0")}</span>
      </div>
      <div className="absolute inset-x-5 bottom-5 text-white md:inset-x-8 md:bottom-8">
        <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-white/70">{project.category_ids.map((id) => catById[id]?.name).filter(Boolean).join(" · ")}</p>
        <div className="mt-3 flex items-end justify-between gap-6">
          <h3 className="font-display text-3xl leading-none md:text-5xl">{project.name}</h3>
          <span className="grid h-12 w-12 shrink-0 place-items-center rounded-full border border-white/30 transition-[background-color,border-color,transform] duration-500 group-hover:rotate-45 group-hover:border-turq group-hover:bg-turq group-hover:text-black" aria-hidden>
            <ArrowUpRight className="h-5 w-5" />
          </span>
        </div>
        <p className="mt-3 max-w-lg text-sm text-white/75 transition-[opacity,transform] duration-500 md:translate-y-2 md:opacity-0 md:group-hover:translate-y-0 md:group-hover:opacity-100">{project.tagline}</p>
      </div>
    </Link>
  );
};

export const SelectedWork = () => {
  const { data } = useFetch("/projects?featured=true");
  return (
    <section id="selected-work" data-testid="selected-work-section" className="container-x py-24 md:py-36">
      <div className="mb-14 flex flex-wrap items-end justify-between gap-8">
        <div>
          <SectionLabel number="01">Selected Work</SectionLabel>
          <h2 className="mt-6 max-w-3xl font-display text-4xl leading-[1] sm:text-5xl lg:text-6xl">Ideas, made visible — and made to work.</h2>
        </div>
        <ButtonLink to="/work" variant="secondary" testId="selected-work-all-link">All projects</ButtonLink>
      </div>
      <div className="grid grid-cols-1 gap-6 md:grid-cols-12 lg:gap-8">
        {(data || []).map((p, i) => (
          <Reveal key={p.id} delay={(i % 2) * 0.1} className={cn(SPANS[i % SPANS.length])}>
            <FeaturedCard project={p} index={i} />
          </Reveal>
        ))}
      </div>
    </section>
  );
};
